import BuildConfig from '$extensionSrc/buildConfig'
import { NO_TIMEOUT } from '$extensionSrc/consts/stageMachineConsts'
import { BlueskySettingKey } from '$extensionSrc/recommendations/recommendationSettingsKeys'
import {
  MONTHS,
  OLDER_THAN_OPTION,
  getOlderThanMonthYear,
} from '$extensionSrc/utils/dateUtils'
import {
  BlueskyRecommendationKey,
  PLATFORM,
  StageType,
} from '$extensionSrc/utils/enums'
import { _debug } from '$extensionSrc/utils/logging'
import { wait } from '$extensionSrc/utils/promises'

import { AUTOMATION_STAGE_STATUSES } from '../genericAutomationStageStatuses'
import * as blueskyRequestUtils from './blueskyRequestUtils'
import { StageDef } from '../automationMachineInterfaces'
import {
  getStoredMachineOverlayStatus,
  setStoredMachineOverlayStatus,
} from '../automationStageMachine'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
} from '../recommendationCardDisplayUtils'
import { getElement, getInitialValueBySettingKey } from '../utils'
import { getCaseInsensitiveXPathTextSelector } from '../xpath'

export const getCurrentValueFn = () => () => ''
export const getIsFulfilledFn = () => () => true
export const getCantUndoFn = () => defaultCantUndoFn
export const getIsDisabledFn = () => defaultIsDisabledFn
export const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

const StageKey = {
  UPDATE_DELETE_GO_TO_PROFILE: 'UPDATE_DELETE_GO_TO_PROFILE',
  UPDATE_DELETE_POSTS: 'UPDATE_DELETE_POSTS',
  UPDATE_REFRESH_FINAL: 'UPDATE_REFRESH_FINAL',
}

async function clickProfileLink() {
  const profileLink = await getElement<HTMLAnchorElement>(
    document,
    `//a[${getCaseInsensitiveXPathTextSelector('aria-label', 'profile')} and @role="link" ]`,
  )

  profileLink.click()
}

async function* getPostsIterator(userId: string, olderThanTimestamp: number) {
  let authorFeedResponse = await blueskyRequestUtils.getAuthorFeed(userId)

  while (authorFeedResponse.feed.length > 0) {
    // Cursor is the timestamp of the last post fetched
    const cursor = authorFeedResponse.cursor

    if (!cursor || (cursor && Date.parse(cursor) < olderThanTimestamp)) {
      for (const post of authorFeedResponse.feed) {
        const postId = post.post.uri.split('/').pop()

        if (post.reason?.by?.did === userId) {
          // It's a repost, get createdAt from reason and id from viewer.repost
          const repostId = post.post.viewer.repost.split('/').pop()
          yield {
            postId: repostId,
            postCreatedAt: post.reason.by.createdAt,
            isRepost: true,
            text: post.post.record.text,
          }
        } else {
          yield {
            postId,
            postCreatedAt: post.post.record.createdAt,
            isRepost: false,
            text: post.post.record.text,
          }
        }
      }
    }

    if (cursor) {
      authorFeedResponse = await blueskyRequestUtils.getAuthorFeed(
        userId,
        authorFeedResponse.cursor,
      )
    } else {
      break
    }
  }
}

function getDeleteOlderThanTimestamp(
  olderThanMonth: number,
  olderThanYear: number,
): number {
  const olderThanTimestamp = new Date(olderThanYear, olderThanMonth).getTime()
  _debug(
    `olderThanTimestamp: ${olderThanTimestamp}`,
    'blueskyDeletePostsBefore.update',
    'd',
  )

  return olderThanTimestamp
}

function buildResultObject(
  totalPostCount: number,
  currentPostCount: number,
  dateOption: OLDER_THAN_OPTION,
  olderThanMonth: number,
  olderThanYear: number,
) {
  const deletedDetail =
    `Your posts, replies, reposts, and quote posts` +
    `${dateOption !== OLDER_THAN_OPTION.NOW ? ` made before ${MONTHS[olderThanMonth]} ${olderThanYear}` : ''}` +
    ' have been deleted.'

  const noPostsFoundDetail =
    `${BuildConfig.TENANT_NAME} was not able to find any posts or replies` +
    `${dateOption !== OLDER_THAN_OPTION.NOW ? ` made before ${MONTHS[olderThanMonth]} ${olderThanYear}` : ''}` +
    ' on your profile.'

  return {
    count: totalPostCount,
    message: `Deleted ${totalPostCount} total posts.`,
    result: {
      title:
        currentPostCount > 0
          ? `You deleted ${currentPostCount} posts`
          : 'No posts deleted',
      detail: currentPostCount > 0 ? deletedDetail : noPostsFoundDetail,
    },
  }
}

async function setUpdateStageStatus(
  dateOption: OLDER_THAN_OPTION,
  olderThanMonth: number,
  olderThanYear: number,
  deletedCount: number,
): Promise<void> {
  const currentStatus = await getStoredMachineOverlayStatus(PLATFORM.BLUESKY)

  const allPostsMsg = `${BuildConfig.TENANT_NAME} is deleting your posts...`
  const olderThanPostsMsg =
    `${BuildConfig.TENANT_NAME} is deleting posts you made before ` +
    `${MONTHS[olderThanMonth]} 1, ${olderThanYear}`

  await setStoredMachineOverlayStatus(PLATFORM.BLUESKY, {
    ...currentStatus,
    title:
      dateOption !== OLDER_THAN_OPTION.NOW ? olderThanPostsMsg : allPostsMsg,
    nDeleted: deletedCount,
  })
}

// No gather or undo stages
export const gatherStageDefs: StageDef[] = []
export const undoStageDefs: StageDef[] = []

export const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_DELETE_GO_TO_PROFILE,
    status: AUTOMATION_STAGE_STATUSES.CHANGING_PAGES,
    action: async () => {
      // Navigate to user profile page. Will not trigger a page refresh on bluesky
      await clickProfileLink()
    },
  },
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_DELETE_POSTS,
    status: AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    urlPattern: 'https://bsky.app/profile/.+',
    timeout: NO_TIMEOUT,
    action: async (machineContext, userContext, additionalMachineData) => {
      const {
        incrementTotalCount,
        initialValues: storedInitialValues,
        isDry,
        settingsSelected,
        updateStoredInitialValues,
        userId,
      } = userContext
      const { resumeData } = additionalMachineData

      const initialValues = getInitialValueBySettingKey(
        storedInitialValues,
        BlueskySettingKey.DELETE_BLUESKY_POSTS_BEFORE,
      )
      const dateSettings =
        settingsSelected?.[BlueskySettingKey.DELETE_BLUESKY_POSTS_BEFORE]

      const selectedDateOption =
        dateSettings?.dateOption || OLDER_THAN_OPTION.NOW
      const [olderThanMonth, olderThanYear] = getOlderThanMonthYear(
        selectedDateOption,
        dateSettings?.sinceDateMonth || new Date().getMonth().toString(),
        dateSettings?.sinceDateYear || new Date().getFullYear().toString(),
      )
      const olderThanTimestamp = getDeleteOlderThanTimestamp(
        olderThanMonth,
        olderThanYear,
      )
      const postsIterator = getPostsIterator(userId, olderThanTimestamp)

      let totalPostCount = initialValues?.count || 0
      let currentCount = resumeData?.data?.deletedCount || 0

      for await (const post of postsIterator) {
        const { postId, isRepost, postCreatedAt, text } = post

        // Ensure the post is older than the selected date
        if (Date.parse(postCreatedAt) < olderThanTimestamp) {
          if (!isDry) {
            const { rateLimitReset, rateLimitRemaining } =
              await blueskyRequestUtils.deletePost(postId, userId, isRepost)

            // Wait a bit if we're up against the rate limit
            if (rateLimitRemaining < 2) {
              _debug(
                `Rate limit low: ${rateLimitRemaining}, resetting at ${rateLimitReset}`,
                'blueskyDeletePostsBefore.update',
                'd',
              )
              const waitTime = new Date(rateLimitReset).getTime() - Date.now()
              if (waitTime > 0) {
                const waitTimeSeconds = Math.round(waitTime / 1000)

                await setStoredMachineOverlayStatus(PLATFORM.BLUESKY, {
                  title: 'Rate Limit Reached',
                  body: `Waiting ${waitTimeSeconds} seconds before continuing...`,
                })
                await wait(waitTime)
              }
            }
          } else {
            // Dry run, log deletion instead of deleting.
            _debug(
              `BLUESKY_DELETE_POSTS Dry Run deleting ${isRepost ? 'repost' : 'post'} ${postId}: ${text}`,
              'blueskyDeletePostsBefore.update',
              'd',
            )
          }

          totalPostCount += 1
          currentCount += 1
          // Update final result message
          await updateStoredInitialValues(
            {
              [BlueskySettingKey.DELETE_BLUESKY_POSTS_BEFORE]:
                buildResultObject(
                  totalPostCount,
                  currentCount,
                  selectedDateOption,
                  olderThanMonth,
                  olderThanYear,
                ),
            },
            BlueskyRecommendationKey.BLUESKY_DELETE_POSTS_BEFORE,
          )

          // Update count stats and save progress in case of pause
          await incrementTotalCount(
            BlueskyRecommendationKey.BLUESKY_DELETE_POSTS_BEFORE,
          )

          await setUpdateStageStatus(
            selectedDateOption,
            olderThanMonth,
            olderThanYear,
            currentCount,
          )

          await resumeData.set({
            ...resumeData.data,
            deletedCount: currentCount,
          })
        } else {
          _debug(
            `Post not deleted due to createdAt ${postCreatedAt} ` +
              `not older than ${olderThanTimestamp}`,
            'blueskyDeletePostsBefore.update',
            'd',
          )
        }
      }

      // Catches case where there were no posts to delete
      await updateStoredInitialValues(
        {
          [BlueskySettingKey.DELETE_BLUESKY_POSTS_BEFORE]: buildResultObject(
            totalPostCount,
            currentCount,
            selectedDateOption,
            olderThanMonth,
            olderThanYear,
          ),
        },
        BlueskyRecommendationKey.BLUESKY_DELETE_POSTS_BEFORE,
      )

      await userContext.updateStatisticsLastCount(
        BlueskyRecommendationKey.BLUESKY_DELETE_POSTS_BEFORE,
        currentCount,
      )
    },
  },
  // Refresh the page to show the content has been deleted
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_REFRESH_FINAL,
    status: AUTOMATION_STAGE_STATUSES.CHANGING_PAGES,
    urlPattern: 'https://bsky.app/profile/.+',
    action: async (_machineContext, _userContext, additionalMachineData) => {
      const { resumeData } = await additionalMachineData
      await resumeData.clear()
      window.location.reload()
    },
  },
]
